import {
  CELL_COLLAPSED,
  CELL_KEY_SEP,
  CELL_WILDCARD,
  findQuantityBreakTier,
  PropertyCharacteristic,
  PropertyCharacteristicGroup,
  quantityBreakKey,
  RateCell,
  RateConfig,
  RateConfigDocument,
  RateFactor
} from "./RateConfig";
import {PreviewItem} from "./PreviewMatrix";
import {computeNumberUp} from "./numberUp";

/**
 * Order-level input shape consumed by {@link RateConfigEvaluator.evaluateOrder}.
 *
 * This is the input the packing rate-config evaluator actually
 * receives at production time. The production rate-config evaluator
 * still uses the legacy `(PreviewItem, quantity)` pair via
 * {@link RateConfigEvaluator.evaluateItem}; the two paths exist
 * side-by-side because a production order's per-book factors run over
 * EACH book independently, whereas packing factors run once per ORDER
 * (with `PER_PACKAGE` factors additionally iterating
 * `packageTypes[]`).
 *
 *  - `quantity`: number of books in the order. Drives `PER_QUANTITY`
 *    factors and the `quantityBreak` characteristic. Defaults to 1
 *    when omitted (allows a pure packing call with only package types).
 *  - `packageTypes`: ordered array of `PackageType.ID`s — one entry
 *    per physical package in the order. May be numeric or string;
 *    converted to a stringified id before resolution. PER_PACKAGE
 *    factors iterate this array and resolve their cells using that
 *    element's `packageType` value (plus order-level characteristics
 *    like `carrierServiceLevel`). Empty / missing array → PER_PACKAGE
 *    factors contribute 0.
 *  - `carrierServiceLevel`: e.g. `'UPS_GROUND'`. Available to
 *    `PER_ORDER` and `PER_PACKAGE` factors only; the validator
 *    forbids book-level kinds from grouping on it.
 *  - `facility`: stringified `Facility.ID` (a.k.a. PrintNode id) the
 *    order is being produced/packed at. Available to every factor
 *    kind — even per-book factors may legitimately differentiate by
 *    facility (e.g. one facility runs the press cheaper). Optional;
 *    defaults to empty string, which only matches wildcard cells.
 *  - `item`: optional per-book template providing values for
 *    book-level characteristics (`interiorType`, `bindingType`, etc.).
 *    Mostly relevant when a packing config has book-level factors
 *    (uncommon but legal) — the evaluator applies them ONCE using
 *    this template, not per-book.
 */
export interface OrderRateInput {
  quantity?: number;
  packageTypes?: (string | number)[];
  carrierServiceLevel?: string;
  facility?: string | number;
  item?: Partial<PreviewItem>;
}

/**
 * Per-row preview result returned by {@link RateConfigEvaluator.evaluateItem}.
 * Mirrors the v2 shape so existing UI (when rebuilt) can keep using it.
 *
 * `infeasible: true` is set when the rating engine would have thrown
 * {@link RateInfeasibleError} for this row. The preview surfaces this as
 * an "infeasible" cell rather than crashing the whole table.
 */
export interface PreviewResult {
  unitCost: number;
  perOrderCost: number;
  totalCost: number;
  infeasible?: boolean;
  /** When `infeasible`, identifies the offending factor + group + cell. */
  infeasibleReason?: {
    factorId: string;
    characteristics: PropertyCharacteristic[];
    comboKey: string;
  };
}

export interface CostTableRow {
  minQty: number;
  maxQty: number;
  result: PreviewResult;
}

/**
 * One line in a per-cell breakdown. Each factor contributes one
 * {@link BreakdownStep}; the result-finalize stage (global minimums +
 * percentage discount) can contribute additional `kind: 'minimum'` /
 * `kind: 'discount'` steps when they actually change a running total
 * so the user sees only the math that mattered for the cell.
 *
 * Field semantics:
 *  - `factorId` / `factorKind`: which factor (or `'config'` for
 *    finalize steps). Used by the UI for grouping + labeling.
 *  - `groupCharacteristics`: the matched group's characteristics, in
 *    storage order. Empty for finalize steps and for factors without
 *    groups (those have only a `baseAmount`).
 *  - `matchedKey`: the cell key the engine resolved to (e.g. `"GRAY|1-19"`),
 *    or `undefined` when no cell matched / not applicable.
 *  - `inheritedFromKey`: when the matched key carries `*` or `-` tokens
 *    at positions where the EXACT key would have had a literal, this
 *    is the exact (would-be) key — useful for explaining "this row
 *    inherited from a wider wildcard".
 *  - `matchedAmount`: the cell's `amount` (the per-unit-of-multiplier
 *    rate), or the factor's `baseAmount` when no group applies.
 *  - `multiplier`: only set for `PER_NUMERIC_PROPERTY` factors —
 *    `Math.ceil(propVal / signatureRoundingMultiple) * signatureRoundingMultiple`
 *    when rounding is configured, else `propVal`. Undefined for other kinds.
 *  - `multiplierLabel`: human-readable explanation of `multiplier`
 *    (e.g. "pages = 200" or "pages rounded up to 16 = 208"). Used
 *    directly in the UI; nothing else parses it.
 *  - `rawContribution`: `matchedAmount * (multiplier ?? 1)`. The
 *    raw factor amount before the per-factor minimum is applied.
 *  - `perItemMinimum`: factor's `perItemMinimum` when set; undefined otherwise.
 *  - `contribution`: `Math.max(rawContribution, perItemMinimum ?? -Infinity)`
 *    — the amount actually added to the running total.
 *  - `targetTotal`: which running total this step modified
 *    (`unit` for PER_ITEM/PER_QUANTITY/PER_NUMERIC_PROPERTY,
 *    `perOrder` for PER_ORDER).
 *  - `note`: free-form explanatory text shown under the line
 *    (e.g. "infeasible cell short-circuited the evaluation" or
 *    "no-match — group contributed 0").
 */
export interface BreakdownStep {
  factorId: string;
  factorKind: 'PER_ORDER' | 'PER_ITEM' | 'PER_QUANTITY' | 'PER_NUMERIC_PROPERTY' | 'PER_PACKAGE' | 'config';
  groupCharacteristics: PropertyCharacteristic[];
  matchedKey?: string;
  inheritedFromKey?: string;
  matchedAmount?: number;
  multiplier?: number;
  multiplierLabel?: string;
  rawContribution?: number;
  perItemMinimum?: number;
  contribution?: number;
  targetTotal: 'unit' | 'perOrder' | 'finalize';
  note?: string;
}

/** Container returned by {@link RateConfigEvaluator.evaluateItemBreakdown}. */
export interface BreakdownResult {
  /** Same shape as {@link evaluateItem}'s return value. */
  result: PreviewResult;
  /** Ordered list of steps the engine took to arrive at `result`. */
  steps: BreakdownStep[];
}

/**
 * Thrown by the engine when a matched cell is `infeasible: true`.
 * The preview path catches this and converts it into
 * {@link PreviewResult.infeasible}; the production rating path
 * propagates it so the HTTP layer can render `400 Bad Request`.
 */
export class RateInfeasibleError extends Error {
  constructor(
    public factorId: string,
    public characteristics: PropertyCharacteristic[],
    public comboKey: string,
    public itemContext: any
  ) {
    super(`Rate infeasible: factor=${factorId} characteristics=${characteristics.join(',')} comboKey=${comboKey}`);
  }
}

/**
 * Pure-functional rate evaluator. v3 — see `docs/contracts/shared/RateConfig.md`.
 *
 * The implementation matches the contract's "Evaluation rules" line by
 * line. All branches are deliberately straightforward; performance is
 * not a concern (the editor evaluates a few dozen preview rows on each
 * keystroke).
 */
export class RateConfigEvaluator {

  /**
   * Evaluate a single (item, quantity) pair against the scope's factors.
   * Returns total cost, with breakdowns. Catches
   * {@link RateInfeasibleError} and surfaces it via the result's
   * `infeasible` flag — callers in the production rating path should
   * call {@link evaluateItemThrowing} instead.
   */
  static evaluateItem(item: PreviewItem, quantity: number, doc: RateConfigDocument, scope: string): PreviewResult {
    try {
      return RateConfigEvaluator.evaluateItemThrowing(item, quantity, doc, scope);
    } catch (e) {
      if (e instanceof RateInfeasibleError) {
        return {
          unitCost: 0,
          perOrderCost: 0,
          totalCost: 0,
          infeasible: true,
          infeasibleReason: {
            factorId: e.factorId,
            characteristics: e.characteristics,
            comboKey: e.comboKey
          }
        };
      }
      throw e;
    }
  }

  /** Like {@link evaluateItem} but propagates `RateInfeasibleError`. */
  static evaluateItemThrowing(item: PreviewItem, quantity: number, doc: RateConfigDocument, scope: string): PreviewResult {
    const config = (doc ? doc[scope] : undefined) as Partial<RateConfig> | undefined;
    if (!config) { return zeroResult(); }

    let unitCost = 0;
    let perOrderCost = 0;

    for (const f of (config.factors || [])) {
      // PER_PACKAGE factors iterate `order.packageTypes[]` and have no
      // meaningful contribution at the (item, quantity) level —
      // {@link evaluateItem} is a per-BOOK preview path. Skip them
      // entirely; callers that need package-aware pricing must use
      // {@link evaluateOrder} instead.
      if (f.kind === 'PER_PACKAGE') { continue; }
      const matched = matchedAmountForFactor(f, item, quantity, config);
      // Apply by kind.
      let factorContrib: number;
      switch (f.kind) {
        case 'PER_ORDER':
          factorContrib = matched;
          if (typeof f.perItemMinimum === 'number') { factorContrib = Math.max(factorContrib, f.perItemMinimum); }
          perOrderCost += factorContrib;
          break;
        case 'PER_ITEM':
        case 'PER_QUANTITY':
          factorContrib = matched;
          if (typeof f.perItemMinimum === 'number') { factorContrib = Math.max(factorContrib, f.perItemMinimum); }
          unitCost += factorContrib;
          break;
        case 'PER_NUMERIC_PROPERTY': {
          const propVal = f.property ? Number((item as any)[f.property] || 0) : 0;
          const m = f.signatureRoundingMultiple;
          const rounded = (typeof m === 'number' && m > 0) ? Math.ceil(propVal / m) * m : propVal;
          factorContrib = matched * rounded;
          if (typeof f.perItemMinimum === 'number') { factorContrib = Math.max(factorContrib, f.perItemMinimum); }
          unitCost += factorContrib;
          break;
        }
      }
    }

    // Global minimums.
    if (config.minimums) {
      if (typeof config.minimums.perItem === 'number')  { unitCost = Math.max(unitCost, config.minimums.perItem); }
      if (typeof config.minimums.perOrder === 'number') { perOrderCost = Math.max(perOrderCost, config.minimums.perOrder); }
    }

    // Percentage discount applied LAST.
    const pct = typeof config.percentageDiscount === 'number' ? config.percentageDiscount : 0;
    if (pct !== 0) {
      const factor = 1 - (pct / 100);
      unitCost *= factor;
      perOrderCost *= factor;
    }

    // Round to currency precision (USD-style, 2 decimals).
    unitCost = round2(unitCost);
    perOrderCost = round2(perOrderCost);
    const totalCost = round2(unitCost * quantity + perOrderCost);
    return {unitCost, perOrderCost, totalCost};
  }

  /**
   * Order-level evaluation entry point. Mirrors the contract's
   * "Evaluation rules" applied to an {@link OrderRateInput}:
   *
   *  - `PER_ORDER` factors contribute once to `perOrderCost`.
   *  - `PER_ITEM` factors contribute their matched amount once to
   *    `unitCost` (which is then multiplied by `quantity`).
   *  - `PER_QUANTITY` factors contribute their matched amount once to
   *    `unitCost` (also multiplied by `quantity` at the end). Semantic
   *    contract: `PER_QUANTITY` is "per book", so the per-book
   *    contribution is the matched amount, scaled by `quantity` in the
   *    final total; this matches the per-item evaluator's behaviour.
   *  - `PER_NUMERIC_PROPERTY` reads `item[property]` from the order's
   *    item template (defaults to 0 if no template is supplied), with
   *    optional signature rounding.
   *  - `PER_PACKAGE` iterates `input.packageTypes[]`; for each entry,
   *    resolves the factor's groups with that entry's `PackageType.ID`
   *    as the `packageType` token, and adds the matched amount to
   *    `perOrderCost`. Empty / missing `packageTypes` → contributes 0.
   *
   * Catches {@link RateInfeasibleError} and surfaces it via the
   * result's `infeasible` flag — callers that need the throw-on-
   * infeasible semantic should call {@link evaluateOrderThrowing}.
   */
  static evaluateOrder(input: OrderRateInput, doc: RateConfigDocument, scope: string): PreviewResult {
    try {
      return RateConfigEvaluator.evaluateOrderThrowing(input, doc, scope);
    } catch (e) {
      if (e instanceof RateInfeasibleError) {
        return {
          unitCost: 0,
          perOrderCost: 0,
          totalCost: 0,
          infeasible: true,
          infeasibleReason: {
            factorId: e.factorId,
            characteristics: e.characteristics,
            comboKey: e.comboKey
          }
        };
      }
      throw e;
    }
  }

  /** Like {@link evaluateOrder} but propagates `RateInfeasibleError`. */
  static evaluateOrderThrowing(input: OrderRateInput, doc: RateConfigDocument, scope: string): PreviewResult {
    const config = (doc ? doc[scope] : undefined) as Partial<RateConfig> | undefined;
    if (!config) { return zeroResult(); }

    const quantity = typeof input.quantity === 'number' && isFinite(input.quantity) && input.quantity > 0
      ? input.quantity : 1;
    const item = makeOrderItem(input);
    const packageTypes = (input.packageTypes || []).map(pt => '' + pt);

    let unitCost = 0;
    let perOrderCost = 0;

    for (const f of (config.factors || [])) {
      switch (f.kind) {
        case 'PER_ORDER': {
          const matched = matchedAmountForFactor(f, item, quantity, config, input);
          let factorContrib = matched;
          if (typeof f.perItemMinimum === 'number') { factorContrib = Math.max(factorContrib, f.perItemMinimum); }
          perOrderCost += factorContrib;
          break;
        }
        case 'PER_ITEM':
        case 'PER_QUANTITY': {
          const matched = matchedAmountForFactor(f, item, quantity, config, input);
          let factorContrib = matched;
          if (typeof f.perItemMinimum === 'number') { factorContrib = Math.max(factorContrib, f.perItemMinimum); }
          unitCost += factorContrib;
          break;
        }
        case 'PER_NUMERIC_PROPERTY': {
          const matched = matchedAmountForFactor(f, item, quantity, config, input);
          const propVal = f.property ? Number((item as any)[f.property] || 0) : 0;
          const m = f.signatureRoundingMultiple;
          const rounded = (typeof m === 'number' && m > 0) ? Math.ceil(propVal / m) * m : propVal;
          let factorContrib = matched * rounded;
          if (typeof f.perItemMinimum === 'number') { factorContrib = Math.max(factorContrib, f.perItemMinimum); }
          unitCost += factorContrib;
          break;
        }
        case 'PER_PACKAGE': {
          // One iteration per element of packageTypes[]. Each iteration
          // resolves the factor's groups with that element's packageType
          // id as the override token (so a single physical UPS_GROUND
          // shipment of three "5" cartons + one "11" oversize pays the
          // sum of three "5"-cell amounts + one "11"-cell amount).
          if (packageTypes.length === 0) {
            // No packages → factor contributes nothing. PER_PACKAGE
            // factors are meaningless for a production-rate evaluation
            // where the caller never supplies `packageTypes`.
            break;
          }
          let factorContrib = 0;
          for (const pt of packageTypes) {
            const matched = matchedAmountForFactor(f, item, quantity, config, input, pt);
            factorContrib += matched;
          }
          if (typeof f.perItemMinimum === 'number') { factorContrib = Math.max(factorContrib, f.perItemMinimum); }
          perOrderCost += factorContrib;
          break;
        }
      }
    }

    // Global minimums.
    if (config.minimums) {
      if (typeof config.minimums.perItem === 'number')  { unitCost = Math.max(unitCost, config.minimums.perItem); }
      if (typeof config.minimums.perOrder === 'number') { perOrderCost = Math.max(perOrderCost, config.minimums.perOrder); }
    }

    // Percentage discount applied LAST.
    const pct = typeof config.percentageDiscount === 'number' ? config.percentageDiscount : 0;
    if (pct !== 0) {
      const factor = 1 - (pct / 100);
      unitCost *= factor;
      perOrderCost *= factor;
    }

    unitCost = round2(unitCost);
    perOrderCost = round2(perOrderCost);
    const totalCost = round2(unitCost * quantity + perOrderCost);
    return {unitCost, perOrderCost, totalCost};
  }

  /**
   * Order-level twin of {@link evaluateItemBreakdown}. Walks the same
   * `evaluateOrder` evaluation path but records one {@link BreakdownStep}
   * per factor (with per-package iteration sub-steps for `PER_PACKAGE`
   * factors so the user can see each package's contribution), plus the
   * synthetic finalize-stage steps for global minimums / percentage
   * discount / infeasibility short-circuit.
   *
   * Step semantics mirror {@link evaluateItemBreakdown}: factors with
   * 0 groups emit one step against the factor's `baseAmount`;
   * single-group factors emit one step with the matched cell key;
   * multi-group factors emit per-group steps + a trailing roll-up
   * step carrying the factor's net contribution. `PER_PACKAGE`
   * factors additionally emit one per-package iteration step so the
   * UI can show "5 × $0.10 + 1 × $0.50 + $0.05 (min)" as a stack
   * (one line per `packageTypes[i]`, then the roll-up).
   *
   * Used by the Rates Preview's "Packing preview" section to render
   * the same per-FactorKind grouped breakdown that per-book cells now
   * show — keeps the explanation surface consistent between the
   * per-item and order-level evaluation paths.
   */
  static evaluateOrderBreakdown(input: OrderRateInput, doc: RateConfigDocument, scope: string): BreakdownResult {
    const steps: BreakdownStep[] = [];
    const config = (doc ? doc[scope] : undefined) as Partial<RateConfig> | undefined;
    if (!config) { return {result: zeroResult(), steps}; }

    const quantity = typeof input.quantity === 'number' && isFinite(input.quantity) && input.quantity > 0
      ? input.quantity : 1;
    const item = makeOrderItem(input);
    const packageTypes = (input.packageTypes || []).map(pt => '' + pt);

    let unitCost = 0;
    let perOrderCost = 0;
    try {
      for (const f of (config.factors || [])) {
        const targetTotal: 'unit' | 'perOrder' =
          (f.kind === 'PER_ORDER' || f.kind === 'PER_PACKAGE') ? 'perOrder' : 'unit';
        const groups = f.groups || [];
        // PER_PACKAGE follows a different shape: ONE outer iteration
        // per element of packageTypes[], each one fully resolving the
        // factor's groups against that package's id. We handle it
        // separately so each iteration emits its own breakdown step.
        //
        // When the factor has MULTIPLE groups, each package iteration
        // additionally emits one per-group "line item" step BEFORE the
        // per-package sum step, so the UI can show "Package 1 of 3:
        // [packageType|facility = 5|*] 2.50 + [carrierServiceLevel =
        // UPS_GROUND] 1.20 = 3.70" instead of a bare per-package
        // total. Per-group steps for PER_PACKAGE carry the package
        // iteration note (`Package N of M: packageType=X`) on every
        // group line so the UI can group them visually under the
        // owning package iteration when folding (the trailing sum
        // step's note ends with ` — package sum` so the fold can
        // detect iteration boundaries deterministically).
        if (f.kind === 'PER_PACKAGE') {
          if (packageTypes.length === 0) {
            steps.push({
              factorId: f.id, factorKind: f.kind, groupCharacteristics: [],
              targetTotal: 'perOrder',
              matchedAmount: 0, rawContribution: 0, contribution: 0,
              note: 'No packageTypes supplied — factor contributed 0.'
            });
            continue;
          }
          let factorSum = 0;
          for (let pi = 0; pi < packageTypes.length; pi++) {
            const pt = packageTypes[pi];
            const pkgNote = `Package ${pi + 1} of ${packageTypes.length}: packageType=${pt}`;
            let packageSum = 0;
            if (groups.length === 0) {
              // No groups — single per-package contribution comes from
              // the factor's baseAmount. Surface it as one step per
              // package so the UI lists every package iteration.
              const baseAmt = typeof f.baseAmount === 'number' ? f.baseAmount : 0;
              packageSum = baseAmt;
              steps.push({
                factorId: f.id, factorKind: f.kind, groupCharacteristics: [],
                matchedKey: pt,
                matchedAmount: baseAmt, rawContribution: baseAmt, contribution: baseAmt,
                targetTotal: 'perOrder',
                note: pkgNote
              });
            } else if (groups.length === 1) {
              // Single-group factor — emit ONE step per package
              // carrying the group's characteristics + matchedKey so
              // the inline expression reads as `[chars=key] amount`.
              const g = groups[0];
              const cell = resolveCellForGroup(g, item, quantity, config, input, pt);
              if (!cell) {
                // No match — emit a 0 step so the package iteration is
                // still visible in the UI.
                steps.push({
                  factorId: f.id, factorKind: f.kind,
                  groupCharacteristics: g.characteristics || [],
                  matchedAmount: 0, rawContribution: 0, contribution: 0,
                  targetTotal: 'perOrder',
                  note: pkgNote + ' — no cell matched in this group'
                });
              } else if (cell.infeasible) {
                throw new RateInfeasibleError(
                  f.id, g.characteristics,
                  rebuildMatchedComboKey(g, item, quantity, config, input, pt) || '',
                  {item, quantity, order: input, packageType: pt}
                );
              } else {
                const amt = typeof cell.amount === 'number' ? cell.amount : 0;
                packageSum += amt;
                const matched = rebuildMatchedComboKey(g, item, quantity, config, input, pt);
                const exact = g.characteristics.map(c => valueForCharacteristic(c, item, quantity, config, input, pt)).join(CELL_KEY_SEP);
                steps.push({
                  factorId: f.id, factorKind: f.kind,
                  groupCharacteristics: g.characteristics || [],
                  matchedKey: matched,
                  inheritedFromKey: matched && matched !== exact ? exact : undefined,
                  matchedAmount: amt,
                  rawContribution: amt,
                  contribution: amt,
                  targetTotal: 'perOrder',
                  note: pkgNote
                });
              }
            } else {
              // Multi-group factor — emit one per-group step per
              // package (carrying the package note on each), then a
              // per-package sum step (whose note ends with ` —
              // package sum`) so the UI fold can group them under
              // their owning package iteration.
              for (let gi = 0; gi < groups.length; gi++) {
                const g = groups[gi];
                const cell = resolveCellForGroup(g, item, quantity, config, input, pt);
                if (!cell) {
                  steps.push({
                    factorId: f.id, factorKind: f.kind,
                    groupCharacteristics: g.characteristics || [],
                    matchedAmount: 0, rawContribution: 0, contribution: 0,
                    targetTotal: 'perOrder',
                    note: pkgNote + ' — no cell matched in this group'
                  });
                  continue;
                }
                if (cell.infeasible) {
                  throw new RateInfeasibleError(
                    f.id, g.characteristics,
                    rebuildMatchedComboKey(g, item, quantity, config, input, pt) || '',
                    {item, quantity, order: input, packageType: pt}
                  );
                }
                const amt = typeof cell.amount === 'number' ? cell.amount : 0;
                packageSum += amt;
                const matched = rebuildMatchedComboKey(g, item, quantity, config, input, pt);
                const exact = g.characteristics.map(c => valueForCharacteristic(c, item, quantity, config, input, pt)).join(CELL_KEY_SEP);
                steps.push({
                  factorId: f.id, factorKind: f.kind,
                  groupCharacteristics: g.characteristics || [],
                  matchedKey: matched,
                  inheritedFromKey: matched && matched !== exact ? exact : undefined,
                  matchedAmount: amt,
                  rawContribution: amt,
                  contribution: amt,
                  targetTotal: 'perOrder',
                  note: pkgNote
                });
              }
              // Per-package sum step (multi-group only — single-group
              // and zero-group already carry the per-package amount on
              // their own step). The trailing ` — package sum` suffix
              // is the deterministic signal the UI fold uses to close
              // this package iteration's sub-parts.
              steps.push({
                factorId: f.id, factorKind: f.kind, groupCharacteristics: [],
                matchedKey: pt,
                matchedAmount: packageSum,
                rawContribution: packageSum,
                contribution: packageSum,
                targetTotal: 'perOrder',
                note: pkgNote + ' — package sum'
              });
            }
            factorSum += packageSum;
          }
          const min = typeof f.perItemMinimum === 'number' ? f.perItemMinimum : undefined;
          const contribution = min !== undefined ? Math.max(factorSum, min) : factorSum;
          perOrderCost += contribution;
          steps.push({
            factorId: f.id, factorKind: f.kind, groupCharacteristics: [],
            matchedAmount: factorSum,
            rawContribution: factorSum,
            perItemMinimum: min,
            contribution,
            targetTotal: 'perOrder',
            note: `Summed across ${packageTypes.length} package${packageTypes.length === 1 ? '' : 's'}.`
          });
          continue;
        }

        // PER_ORDER / PER_ITEM / PER_QUANTITY / PER_NUMERIC_PROPERTY
        // — same per-group accumulation pattern as evaluateItemBreakdown.
        let matchedAmount: number;
        let matchedKey: string | undefined;
        let groupCharacteristics: PropertyCharacteristic[] = [];
        let inheritedFromKey: string | undefined;
        let noteForFactor: string | undefined;
        if (groups.length === 0) {
          matchedAmount = typeof f.baseAmount === 'number' ? f.baseAmount : 0;
          noteForFactor = matchedAmount === 0
            ? 'No groups and no baseAmount; contributes nothing.'
            : 'No groups; uses baseAmount directly.';
        } else {
          let sum = 0;
          let firstKey: string | undefined;
          let firstInherited: string | undefined;
          let firstChars: PropertyCharacteristic[] = [];
          let firstAmount = 0;
          for (let gi = 0; gi < groups.length; gi++) {
            const g = groups[gi];
            const cell = resolveCellForGroup(g, item, quantity, config, input);
            if (!cell) {
              if (groups.length > 1) {
                steps.push({
                  factorId: f.id, factorKind: f.kind,
                  groupCharacteristics: g.characteristics || [],
                  targetTotal,
                  matchedAmount: 0, rawContribution: 0, contribution: 0,
                  note: 'No cell matched in this group — contributed 0.'
                });
              }
              continue;
            }
            if (cell.infeasible) {
              throw new RateInfeasibleError(
                f.id, g.characteristics,
                rebuildMatchedComboKey(g, item, quantity, config, input) || '',
                {item, quantity, order: input}
              );
            }
            const amt = typeof cell.amount === 'number' ? cell.amount : 0;
            sum += amt;
            const matched = rebuildMatchedComboKey(g, item, quantity, config, input);
            const exact = g.characteristics.map(c => valueForCharacteristic(c, item, quantity, config, input)).join(CELL_KEY_SEP);
            if (groups.length > 1) {
              steps.push({
                factorId: f.id, factorKind: f.kind,
                groupCharacteristics: g.characteristics || [],
                matchedKey: matched,
                inheritedFromKey: matched && matched !== exact ? exact : undefined,
                matchedAmount: amt,
                rawContribution: amt,
                contribution: amt,
                targetTotal
              });
            } else {
              firstKey = matched;
              firstInherited = matched && matched !== exact ? exact : undefined;
              firstChars = g.characteristics || [];
              firstAmount = amt;
            }
          }
          matchedAmount = sum;
          if (groups.length === 1) {
            matchedKey = firstKey;
            inheritedFromKey = firstInherited;
            groupCharacteristics = firstChars;
            matchedAmount = firstAmount;
          }
        }
        // PER_NUMERIC_PROPERTY: multiply by the item's numeric property.
        let multiplier: number | undefined;
        let multiplierLabel: string | undefined;
        let rawContribution = matchedAmount;
        if (f.kind === 'PER_NUMERIC_PROPERTY') {
          const propVal = f.property ? Number((item as any)[f.property] || 0) : 0;
          const m = f.signatureRoundingMultiple;
          multiplier = (typeof m === 'number' && m > 0) ? Math.ceil(propVal / m) * m : propVal;
          multiplierLabel = (typeof m === 'number' && m > 0 && multiplier !== propVal)
            ? `${f.property} ${propVal} rounded up to nearest ${m} = ${multiplier}`
            : `${f.property} = ${propVal}`;
          rawContribution = matchedAmount * multiplier;
        }
        const minApplied = typeof f.perItemMinimum === 'number' ? f.perItemMinimum : undefined;
        const contribution = minApplied !== undefined ? Math.max(rawContribution, minApplied) : rawContribution;
        if (targetTotal === 'unit') { unitCost += contribution; } else { perOrderCost += contribution; }
        if (!(groups.length > 1 && contribution === 0 && !noteForFactor)) {
          steps.push({
            factorId: f.id, factorKind: f.kind,
            groupCharacteristics: groupCharacteristics,
            matchedKey, inheritedFromKey, matchedAmount,
            multiplier, multiplierLabel,
            rawContribution,
            perItemMinimum: minApplied,
            contribution,
            targetTotal,
            note: noteForFactor
          });
        }
      }
    } catch (e) {
      if (e instanceof RateInfeasibleError) {
        steps.push({
          factorId: e.factorId, factorKind: 'config',
          groupCharacteristics: e.characteristics,
          matchedKey: e.comboKey,
          targetTotal: 'finalize',
          note: 'Infeasible cell — short-circuited evaluation; subsequent factors not applied.'
        });
        return {
          result: {
            unitCost: 0, perOrderCost: 0, totalCost: 0,
            infeasible: true,
            infeasibleReason: {factorId: e.factorId, characteristics: e.characteristics, comboKey: e.comboKey}
          },
          steps
        };
      }
      throw e;
    }
    // Global minimums.
    if (config.minimums) {
      if (typeof config.minimums.perItem === 'number' && config.minimums.perItem > unitCost) {
        steps.push({
          factorId: '', factorKind: 'config', groupCharacteristics: [],
          targetTotal: 'finalize',
          note: `Global perItem minimum ${config.minimums.perItem} bumped unit cost from ${round2(unitCost)} to ${config.minimums.perItem}.`
        });
        unitCost = config.minimums.perItem;
      }
      if (typeof config.minimums.perOrder === 'number' && config.minimums.perOrder > perOrderCost) {
        steps.push({
          factorId: '', factorKind: 'config', groupCharacteristics: [],
          targetTotal: 'finalize',
          note: `Global perOrder minimum ${config.minimums.perOrder} bumped per-order cost from ${round2(perOrderCost)} to ${config.minimums.perOrder}.`
        });
        perOrderCost = config.minimums.perOrder;
      }
    }
    // Percentage discount.
    const pct = typeof config.percentageDiscount === 'number' ? config.percentageDiscount : 0;
    if (pct !== 0) {
      const factor = 1 - (pct / 100);
      const beforeUnit = unitCost;
      const beforeOrder = perOrderCost;
      unitCost *= factor;
      perOrderCost *= factor;
      steps.push({
        factorId: '', factorKind: 'config', groupCharacteristics: [],
        targetTotal: 'finalize',
        note: `${pct}% discount: unit ${round2(beforeUnit)} → ${round2(unitCost)}; perOrder ${round2(beforeOrder)} → ${round2(perOrderCost)}.`
      });
    }
    const u = round2(unitCost);
    const po = round2(perOrderCost);
    return {
      result: {unitCost: u, perOrderCost: po, totalCost: round2(u * quantity + po)},
      steps
    };
  }

  /**
   * Evaluate the document over a fixed list of quantity tiers (for the
   * preview's tier-column rendering). Each row is the result for
   * `(item, midpoint(tier))`.
   */
  static evaluateBreaks(item: PreviewItem, doc: RateConfigDocument, scope: string,
                       tiers?: { minQty: number; maxQty: number }[]): CostTableRow[] {
    const config = (doc ? doc[scope] : undefined) as Partial<RateConfig> | undefined;
    const useTiers = (tiers && tiers.length > 0)
      ? tiers
      : (config && config.defaultQuantityBreaks && config.defaultQuantityBreaks.length > 0
        ? config.defaultQuantityBreaks
        : []);
    const out: CostTableRow[] = [];
    for (const t of useTiers) {
      const qty = t.maxQty >= Number.MAX_SAFE_INTEGER ? t.minQty : Math.max(t.minQty, Math.min(t.maxQty, t.minQty));
      const result = RateConfigEvaluator.evaluateItem(item, qty, doc, scope);
      out.push({minQty: t.minQty, maxQty: t.maxQty, result});
    }
    return out;
  }

  /**
   * Evaluate one (item, quantity) the same way {@link evaluateItem}
   * does, but collect a step-by-step trace of every factor's
   * contribution (matched cell key, raw amount, multiplier, per-factor
   * minimum, running totals) plus the final-stage steps for global
   * minimums and percentage discount when they actually change a
   * running total.
   *
   * Intended ONLY for editor UIs that want to surface "why is this
   * cell this number?" — production rating callers should use
   * {@link evaluateItem} / {@link evaluateItemThrowing}.
   *
   * Catches {@link RateInfeasibleError} so the breakdown can also
   * explain WHY a cell is infeasible (which factor + group + key
   * refused the combo), via a single `note`-bearing step.
   */
  static evaluateItemBreakdown(item: PreviewItem, quantity: number,
                               doc: RateConfigDocument, scope: string): BreakdownResult {
    const steps: BreakdownStep[] = [];
    const config = (doc ? doc[scope] : undefined) as Partial<RateConfig> | undefined;
    if (!config) {
      return {result: zeroResult(), steps};
    }
    let unitCost = 0;
    let perOrderCost = 0;
    try {
      for (const f of (config.factors || [])) {
        // PER_PACKAGE factors are not meaningful in the per-book
        // breakdown path. They iterate `order.packageTypes[]` which
        // does not exist in this scope of evaluation; the breakdown
        // surfaces them via {@link evaluateOrderBreakdown} instead.
        //
        // Emit a placeholder step (rather than silently skipping) so
        // the per-cell breakdown panel surfaces a "Per package"
        // section with a note pointing users at the Packing preview
        // for the actual per-package math. Without this hint users
        // who scrolled to a cell to understand a price thought the
        // PER_PACKAGE factor was being ignored entirely.
        if (f.kind === 'PER_PACKAGE') {
          steps.push({
            factorId: f.id, factorKind: f.kind, groupCharacteristics: [],
            matchedAmount: 0, rawContribution: 0, contribution: 0,
            targetTotal: 'perOrder',
            note: 'Per-package factor — surfaced in the Packing preview section above (per-book breakdown can\'t evaluate per-package math without an order\'s packageTypes[]).'
          });
          continue;
        }
        const groups = f.groups || [];
        let matchedAmount: number;
        let matchedKey: string | undefined;
        let groupCharacteristics: PropertyCharacteristic[] = [];
        let inheritedFromKey: string | undefined;
        let noteForFactor: string | undefined;
        if (groups.length === 0) {
          // Factor has no groups — use baseAmount directly.
          matchedAmount = typeof f.baseAmount === 'number' ? f.baseAmount : 0;
          if (matchedAmount === 0) {
            noteForFactor = 'No groups and no baseAmount; contributes nothing.';
          } else {
            noteForFactor = 'No groups; uses baseAmount directly.';
          }
        } else {
          // Sum across all groups (mirrors matchedAmountForFactor).
          // For the breakdown we attribute the SUM to a single step
          // when there's exactly one group (most common case), or
          // emit one step per group otherwise.
          let sum = 0;
          let firstKey: string | undefined;
          let firstInherited: string | undefined;
          let firstChars: PropertyCharacteristic[] = [];
          let firstAmount = 0;
          for (let gi = 0; gi < groups.length; gi++) {
            const g = groups[gi];
            const cell = resolveCellForGroup(g, item, quantity, config);
            if (!cell) {
              if (groups.length > 1) {
                steps.push({
                  factorId: f.id, factorKind: f.kind,
                  groupCharacteristics: g.characteristics || [],
                  targetTotal: f.kind === 'PER_ORDER' ? 'perOrder' : 'unit',
                  matchedAmount: 0, rawContribution: 0, contribution: 0,
                  note: 'No cell matched in this group — contributed 0.'
                });
              }
              continue;
            }
            if (cell.infeasible) {
              throw new RateInfeasibleError(
                f.id, g.characteristics,
                rebuildMatchedComboKey(g, item, quantity, config) || '',
                {item, quantity}
              );
            }
            const amt = typeof cell.amount === 'number' ? cell.amount : 0;
            sum += amt;
            const matched = rebuildMatchedComboKey(g, item, quantity, config);
            const exact = g.characteristics.map(c => valueForCharacteristic(c, item, quantity, config)).join(CELL_KEY_SEP);
            if (groups.length > 1) {
              steps.push({
                factorId: f.id, factorKind: f.kind,
                groupCharacteristics: g.characteristics || [],
                matchedKey: matched,
                inheritedFromKey: matched && matched !== exact ? exact : undefined,
                matchedAmount: amt,
                rawContribution: amt,
                contribution: amt,
                targetTotal: f.kind === 'PER_ORDER' ? 'perOrder' : 'unit'
              });
            } else {
              firstKey = matched;
              firstInherited = matched && matched !== exact ? exact : undefined;
              firstChars = g.characteristics || [];
              firstAmount = amt;
            }
          }
          matchedAmount = sum;
          if (groups.length === 1) {
            matchedKey = firstKey;
            inheritedFromKey = firstInherited;
            groupCharacteristics = firstChars;
            matchedAmount = firstAmount;
          }
        }
        // Apply the per-kind transform.
        let multiplier: number | undefined;
        let multiplierLabel: string | undefined;
        let rawContribution = matchedAmount;
        if (f.kind === 'PER_NUMERIC_PROPERTY') {
          const propVal = f.property ? Number((item as any)[f.property] || 0) : 0;
          const m = f.signatureRoundingMultiple;
          multiplier = (typeof m === 'number' && m > 0) ? Math.ceil(propVal / m) * m : propVal;
          multiplierLabel = (typeof m === 'number' && m > 0 && multiplier !== propVal)
            ? `${f.property} ${propVal} rounded up to nearest ${m} = ${multiplier}`
            : `${f.property} = ${propVal}`;
          rawContribution = matchedAmount * multiplier;
        }
        const minApplied = typeof f.perItemMinimum === 'number' ? f.perItemMinimum : undefined;
        const contribution = minApplied !== undefined ? Math.max(rawContribution, minApplied) : rawContribution;
        const target: 'unit' | 'perOrder' = f.kind === 'PER_ORDER' ? 'perOrder' : 'unit';
        if (target === 'unit') { unitCost += contribution; } else { perOrderCost += contribution; }
        if (!(groups.length > 1 && contribution === 0 && !noteForFactor)) {
          // Skip emitting a single summary step when groups>1 already
          // emitted per-group steps AND there's nothing extra to say
          // (no minimum bump, no multiplier). Otherwise emit a roll-up
          // step so the user always sees the factor's net contribution.
          steps.push({
            factorId: f.id, factorKind: f.kind,
            groupCharacteristics: groupCharacteristics,
            matchedKey, inheritedFromKey, matchedAmount,
            multiplier, multiplierLabel,
            rawContribution,
            perItemMinimum: minApplied,
            contribution,
            targetTotal: target,
            note: noteForFactor
          });
        }
      }
    } catch (e) {
      if (e instanceof RateInfeasibleError) {
        steps.push({
          factorId: e.factorId, factorKind: 'config',
          groupCharacteristics: e.characteristics,
          matchedKey: e.comboKey,
          targetTotal: 'finalize',
          note: 'Infeasible cell — short-circuited evaluation; subsequent factors not applied.'
        });
        return {
          result: {
            unitCost: 0, perOrderCost: 0, totalCost: 0,
            infeasible: true,
            infeasibleReason: {factorId: e.factorId, characteristics: e.characteristics, comboKey: e.comboKey}
          },
          steps
        };
      }
      throw e;
    }
    // Global minimums.
    if (config.minimums) {
      if (typeof config.minimums.perItem === 'number' && config.minimums.perItem > unitCost) {
        steps.push({
          factorId: '', factorKind: 'config', groupCharacteristics: [],
          targetTotal: 'finalize',
          note: `Global perItem minimum ${config.minimums.perItem} bumped unit cost from ${round2(unitCost)} to ${config.minimums.perItem}.`
        });
        unitCost = config.minimums.perItem;
      }
      if (typeof config.minimums.perOrder === 'number' && config.minimums.perOrder > perOrderCost) {
        steps.push({
          factorId: '', factorKind: 'config', groupCharacteristics: [],
          targetTotal: 'finalize',
          note: `Global perOrder minimum ${config.minimums.perOrder} bumped per-order cost from ${round2(perOrderCost)} to ${config.minimums.perOrder}.`
        });
        perOrderCost = config.minimums.perOrder;
      }
    }
    // Percentage discount.
    const pct = typeof config.percentageDiscount === 'number' ? config.percentageDiscount : 0;
    if (pct !== 0) {
      const factor = 1 - (pct / 100);
      const beforeUnit = unitCost;
      const beforeOrder = perOrderCost;
      unitCost *= factor;
      perOrderCost *= factor;
      steps.push({
        factorId: '', factorKind: 'config', groupCharacteristics: [],
        targetTotal: 'finalize',
        note: `${pct}% discount: unit ${round2(beforeUnit)} → ${round2(unitCost)}; perOrder ${round2(beforeOrder)} → ${round2(perOrderCost)}.`
      });
    }
    const u = round2(unitCost);
    const po = round2(perOrderCost);
    return {
      result: {unitCost: u, perOrderCost: po, totalCost: round2(u * quantity + po)},
      steps
    };
  }

  /**
   * Merge an OVERRIDE document on top of a BASE document. Per the
   * contract: scalars replace, factors merge by id (same-id replaces;
   * new ids append), defaultQuantityBreaks replaces wholesale,
   * minimums shallow-merges.
   *
   * Because v3 ids are auto-derived from the factor signature, an
   * override that changes a factor's `groups[]` shape produces a
   * different id and will APPEND rather than replace. Callers must
   * keep the override's signature aligned with the base for replace
   * semantics; only `cells[*].amount` and `cells[*].infeasible` may
   * differ.
   */
  static mergeOverride(base: RateConfigDocument, override: RateConfigDocument | undefined | null): RateConfigDocument {
    if (!override) { return base; }
    const out: RateConfigDocument = {...base};
    for (const scope of Object.keys(override)) {
      // Editor-only preview axes and the server-facing `facilities`
      // ID list are top-level `number[]` fields — pass them through
      // as-is (override wins). See `docs/contracts/shared/RateConfig.md`
      // and `docs/contracts/inbox/_processed/2026-07-08-rateconfig-facilities-field.md`
      // for the `facilities` contract.
      if (scope === '_previewPageCounts'
          || scope === '_previewBookQuantities'
          || scope === 'facilities') {
        out[scope] = (override as any)[scope];
        continue;
      }
      const baseScope = (base[scope] || {}) as Partial<RateConfig>;
      const ovScope   = ((override as any)[scope] || {}) as Partial<RateConfig>;
      const merged: Partial<RateConfig> = {...baseScope, ...ovScope};
      // Factors merged by id.
      const baseFactors = baseScope.factors || [];
      const ovFactors = ovScope.factors || [];
      if (ovFactors.length > 0 || baseFactors.length > 0) {
        const idIndex = new Map<string, RateFactor>();
        for (const f of baseFactors) { idIndex.set(f.id, f); }
        for (const f of ovFactors)   { idIndex.set(f.id, f); }
        merged.factors = Array.from(idIndex.values());
      }
      // Minimums shallow-merge.
      if (baseScope.minimums || ovScope.minimums) {
        merged.minimums = {...(baseScope.minimums || {}), ...(ovScope.minimums || {})};
      }
      // defaultQuantityBreaks: override wins wholesale.
      if (ovScope.defaultQuantityBreaks) {
        merged.defaultQuantityBreaks = ovScope.defaultQuantityBreaks;
      }
      out[scope] = merged;
    }
    return out;
  }
}

// ── helpers ──────────────────────────────────────────────────────────

/**
 * Resolve a cell for one group given an explicit token tuple
 * (`exactValues[i]` is the literal value, `'*'`, or `'-'` at storage
 * position `i`). This is the same algorithm as
 * {@link resolveCellForGroup} but works directly off the cells map and
 * token tuple, so editor UIs can preview what the engine WOULD return
 * for a coordinate without having to fabricate a {@link PreviewItem}.
 *
 * The returned object includes:
 *  - `cell`: the matched cell (may be `undefined` if nothing resolves);
 *  - `key`:  the storage key of the matched cell (undefined if none);
 *  - `inherited`: `true` when the matched key differs from
 *    `exactValues.join('|')` (i.e. the value came from a wider
 *    wildcard / collapsed sibling, not the exact key itself).
 *
 * Used by the group-matrix editor to display the value the engine
 * would resolve for an unauthored cell (so the user doesn't see a
 * misleading `0` when a wider `*: infeasible` would actually apply).
 */
export function resolveCellForExactValues(
  cells: { [k: string]: RateCell } | undefined,
  exactValues: string[]
): { cell: RateCell | undefined; key: string | undefined; inherited: boolean } {
  if (!cells) { return {cell: undefined, key: undefined, inherited: false}; }
  const stub = {cells} as PropertyCharacteristicGroup;
  const n = exactValues.length;
  const total = 1 << n;
  const exactKey = exactValues.join(CELL_KEY_SEP);
  for (let popCount = 0; popCount <= n; popCount++) {
    for (let mask = 0; mask < total; mask++) {
      if (bitCount(mask) !== popCount) { continue; }
      const cell = lookupCellAtMask(stub, exactValues, mask, n);
      if (cell && (cell.infeasible || typeof cell.amount === 'number')) {
        const key = matchedKeyAtMask(stub, exactValues, mask, n);
        return {cell, key, inherited: key !== exactKey};
      }
    }
  }
  return {cell: undefined, key: undefined, inherited: false};
}

function zeroResult(): PreviewResult {
  return {unitCost: 0, perOrderCost: 0, totalCost: 0};
}

function round2(v: number): number {
  return Math.round((v + Number.EPSILON) * 100) / 100;
}

function matchedAmountForFactor(f: RateFactor, item: PreviewItem, quantity: number,
                                config: Partial<RateConfig>,
                                order?: OrderRateInput, packageTypeOverride?: string): number {
  const groups = f.groups || [];
  if (groups.length === 0) {
    return typeof f.baseAmount === 'number' ? f.baseAmount : 0;
  }
  let sum = 0;
  for (const g of groups) {
    const cell = resolveCellForGroup(g, item, quantity, config, order, packageTypeOverride);
    if (!cell) {
      // Engine treats no-match as zero for that group (validator should
      // catch incomplete coverage, but never crash production pricing).
      continue;
    }
    if (cell.infeasible) {
      throw new RateInfeasibleError(
        f.id,
        g.characteristics,
        // Best-effort: rebuild the matched key for the error (re-resolve to find it).
        rebuildMatchedComboKey(g, item, quantity, config, order, packageTypeOverride) || '',
        {item, quantity, order, packageType: packageTypeOverride}
      );
    }
    if (typeof cell.amount === 'number') { sum += cell.amount; }
  }
  return sum;
}

/**
 * Resolve one group to a cell.
 *
 * Priority is fewest-wildcards-first (most specific match wins). At each
 * wildcarded position both `*` and `-` are tried (with `*` taking
 * deterministic precedence when both happen to be authored — the
 * validator forbids that case but we tolerate it here).
 *
 * A cell that exists but is "empty" (no `amount` and no `infeasible`)
 * is considered a no-op punch-through: resolution CONTINUES to the next
 * less-specific key so a wider-wildcard cell can supply the value. This
 * matches the contract's "empty cell falls back" rule and lets authors
 * carve precise exceptions out of a wide default.
 */
function resolveCellForGroup(g: PropertyCharacteristicGroup, item: PreviewItem, quantity: number,
                             config: Partial<RateConfig>,
                             order?: OrderRateInput, packageTypeOverride?: string): RateCell | undefined {
  const exactValues = g.characteristics.map(c => valueForCharacteristic(c, item, quantity, config, order, packageTypeOverride));
  const n = exactValues.length;
  const total = 1 << n;
  for (let popCount = 0; popCount <= n; popCount++) {
    for (let mask = 0; mask < total; mask++) {
      if (bitCount(mask) !== popCount) { continue; }
      const cell = lookupCellAtMask(g, exactValues, mask, n);
      // Skip empty/missing cells — let resolution fall through to a
      // wider-wildcard layer that does carry an amount or infeasible.
      if (cell && (cell.infeasible || typeof cell.amount === 'number')) { return cell; }
    }
  }
  return undefined;
}

function rebuildMatchedComboKey(g: PropertyCharacteristicGroup, item: PreviewItem, quantity: number,
                                config: Partial<RateConfig>,
                                order?: OrderRateInput, packageTypeOverride?: string): string | undefined {
  const exactValues = g.characteristics.map(c => valueForCharacteristic(c, item, quantity, config, order, packageTypeOverride));
  const n = exactValues.length;
  const total = 1 << n;
  for (let popCount = 0; popCount <= n; popCount++) {
    for (let mask = 0; mask < total; mask++) {
      if (bitCount(mask) !== popCount) { continue; }
      const key = matchedKeyAtMask(g, exactValues, mask, n);
      if (key !== undefined) { return key; }
    }
  }
  return undefined;
}

/**
 * Try a single wildcard mask against the group's cells. Tokens at masked
 * positions are looked up as `'*'` first, then `'-'` (deterministic
 * `*`-wins precedence). Returns the first cell found; ignores
 * empty-cell vs valued-cell distinction (that's the caller's concern).
 */
function lookupCellAtMask(g: PropertyCharacteristicGroup, exactValues: string[],
                          mask: number, n: number): RateCell | undefined {
  const tokens: string[] = new Array(n);
  for (let i = 0; i < n; i++) {
    tokens[i] = (mask & (1 << i)) ? CELL_WILDCARD : exactValues[i];
  }
  const star = g.cells[tokens.join(CELL_KEY_SEP)];
  if (star) { return star; }
  // Try each masked position swapped to '-'. We iterate variants
  // because the user could have authored mixed '*' and '-' tokens
  // across positions in a single key. Cost is at most 2^popCount per
  // mask; popCount is small (group axes ≤ 6).
  return lookupCellAtMaskWithDashCombos(g, tokens, mask, n);
}

function matchedKeyAtMask(g: PropertyCharacteristicGroup, exactValues: string[],
                          mask: number, n: number): string | undefined {
  const tokens: string[] = new Array(n);
  for (let i = 0; i < n; i++) {
    tokens[i] = (mask & (1 << i)) ? CELL_WILDCARD : exactValues[i];
  }
  const k = tokens.join(CELL_KEY_SEP);
  if (g.cells[k]) { return k; }
  return matchedKeyAtMaskWithDashCombos(g, tokens, mask, n);
}

/** Enumerate '*' / '-' combinations at the masked positions; return first matching cell. */
function lookupCellAtMaskWithDashCombos(g: PropertyCharacteristicGroup, baseTokens: string[],
                                        mask: number, n: number): RateCell | undefined {
  // Collect masked positions.
  const masked: number[] = [];
  for (let i = 0; i < n; i++) { if (mask & (1 << i)) { masked.push(i); } }
  const m = masked.length;
  if (m === 0) { return undefined; }
  const variants = 1 << m;
  // variant 0 was already tried by caller (all '*'); start at 1.
  for (let v = 1; v < variants; v++) {
    const tokens = baseTokens.slice();
    for (let j = 0; j < m; j++) {
      if (v & (1 << j)) { tokens[masked[j]] = CELL_COLLAPSED; }
    }
    const cell = g.cells[tokens.join(CELL_KEY_SEP)];
    if (cell) { return cell; }
  }
  return undefined;
}

function matchedKeyAtMaskWithDashCombos(g: PropertyCharacteristicGroup, baseTokens: string[],
                                        mask: number, n: number): string | undefined {
  const masked: number[] = [];
  for (let i = 0; i < n; i++) { if (mask & (1 << i)) { masked.push(i); } }
  const m = masked.length;
  if (m === 0) { return undefined; }
  const variants = 1 << m;
  for (let v = 1; v < variants; v++) {
    const tokens = baseTokens.slice();
    for (let j = 0; j < m; j++) {
      if (v & (1 << j)) { tokens[masked[j]] = CELL_COLLAPSED; }
    }
    const k = tokens.join(CELL_KEY_SEP);
    if (g.cells[k]) { return k; }
  }
  return undefined;
}

function valueForCharacteristic(c: PropertyCharacteristic, item: PreviewItem, quantity: number,
                                config: Partial<RateConfig>, order?: OrderRateInput, packageTypeOverride?: string): string {
  switch (c) {
    case 'bindingType':  return (item as any).bindingType || '';
    case 'interiorType': return (item as any).interiorType || '';
    case 'laminateType': return (item as any).laminateType || '';
    case 'paperType':    return (item as any).paperType || '';
    case 'packageType':  return packageTypeOverride != null ? packageTypeOverride : ((item as any).packageType || '');
    case 'carrierServiceLevel': return (order && order.carrierServiceLevel) ? order.carrierServiceLevel : '';
    // facility id is an order-level constant. We accept it from EITHER
    // the order input (the packing / order-level evaluator path) or
    // from the item itself (the per-item evaluator path the production
    // pipeline uses — the server's per-facility table loop assigns
    // the relevant facility id onto each synthesized item). Stringify
    // either source so a numeric `Facility.ID` matches a `'17'` cell
    // key authored in the editor.
    case 'facility': {
      const fromOrder = order && (order as any).facility;
      if (fromOrder != null && fromOrder !== '') { return '' + fromOrder; }
      const fromItem = (item as any).facility;
      return fromItem != null && fromItem !== '' ? '' + fromItem : '';
    }
    case 'numberUp':     return computeNumberUp((item as any).width, (item as any).height);
    case 'quantityBreak': {
      const tier = findQuantityBreakTier(quantity, config.defaultQuantityBreaks);
      return tier ? quantityBreakKey(tier) : '';
    }
  }
}

function bitCount(n: number): number {
  let c = 0;
  while (n) { c += (n & 1); n >>>= 1; }
  return c;
}

/**
 * Build a synthetic {@link PreviewItem} from an {@link OrderRateInput}.
 *
 * The vast majority of packing-rate configs do not group on book-level
 * characteristics (interiorType / paperType / pages / etc.), so the
 * synthetic item simply fills those fields with empty / zero values
 * and they resolve via the group's `*` (or `-`) wildcard cell. When
 * the caller DOES supply `input.item`, those values override the
 * defaults so book-aware packing configs can still match.
 */
function makeOrderItem(input: OrderRateInput): PreviewItem {
  const src = input.item || {};
  return {
    pages:        Number((src as any).pages || 0),
    width:        Number((src as any).width || 0),
    height:       Number((src as any).height || 0),
    weight:       Number((src as any).weight || 0),
    thickness:    Number((src as any).thickness || 0),
    caseQuantity: Number((src as any).caseQuantity || 0),
    interiorType: (src as any).interiorType || '',
    bindingType:  (src as any).bindingType  || '',
    laminateType: (src as any).laminateType || '',
    paperType:    (src as any).paperType    || '',
    packageType:  (src as any).packageType  || '',
    // facility flows in through OrderRateInput.facility (read directly
    // by valueForCharacteristic), but we also forward an item-level
    // override here so a caller that sets `input.item.facility`
    // without setting `input.facility` still resolves the right
    // facility cell. The two paths are equivalent at resolution time;
    // `input.facility` wins when both are present.
    facility:     (src as any).facility     || ''
  };
}

